import React, { useState, useEffect } from 'react';
import {
  DialogContentText,
  Skeleton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Box,
  Chip,
  Select,
  MenuItem,
  TextField,
  Button,
  InputLabel,
  FormControl,
  Tooltip,
} from '@mui/material';

import { formatEnumToLabel } from '../../utils/formatEnumToLabel';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import {
  packagerIdForDocumentsModalAtom,
  packageEntriesDrilldownAtom,
} from '../../atoms/packager-atoms';
import { useAtom } from 'jotai';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import { useParams } from 'react-router';
import useGetPackageEntries from '../hooks/packages/useGetPackageEntries';
import { LinkTableStyled } from '../common/StyledComponents';
import {
  DocumentDataStatusFilters,
  PackageEntry,
  PackageEntryStatus,
} from '../../types/packager-types';
import PackageEntryStatusChange from './package/PackageEntryStatusChange';
import { useGetPackage } from '../hooks/packages/useGetPackage';
import { isEntryEditable } from '../../utils/packageEditability';
import DocumentDataStatusChange from './package/DocumentDataStatusChange';
import { standardTableStyles } from '../common/tableStyles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const PackagerDocuments: React.FC = () => {
  const [packagerIdForDocumentsModal, setPackagerIdForDocumentsModal] = useAtom(
    packagerIdForDocumentsModalAtom
  );
  const [drilldown, setDrilldown] = useAtom(packageEntriesDrilldownAtom);

  const { dpid } = useParams<{ dpid: string }>();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [statusInput, setStatusInput] = useState<PackageEntryStatus | undefined>(undefined);
  const [docIdInput, setDocIdInput] = useState<string>('');

  const [appliedStatus, setAppliedStatus] = useState<PackageEntryStatus | undefined>(undefined);
  const [appliedDocumentId, setAppliedDocumentId] = useState<string>('');

  // A completeness drill-down takes precedence: it seeds the filter from the API's
  // param dict. The manual filters are hidden while it's active.
  const effectivePackageId = drilldown?.packageId ?? packagerIdForDocumentsModal;

  useEffect(() => {
    setPage(0);
  }, [packagerIdForDocumentsModal, drilldown]);

  const { data: currentPackage } = useGetPackage(effectivePackageId || undefined);
  // Unknown until the package loads; the API still enforces the rule either way.
  const entryEditDisabledReason =
    currentPackage && !isEntryEditable(currentPackage.status, currentPackage.transmitted_at)
      ? `Package ${currentPackage.transmitted_at ? 'has been delivered' : `is ${currentPackage.status}`} ` +
        'and can no longer be edited in place. ' +
        'Create a new package from it to make changes.'
      : null;

  const { data, isLoading, isFetching } = useGetPackageEntries({
    packageId: effectivePackageId || undefined,
    status: drilldown ? undefined : appliedStatus,
    documentId: drilldown ? undefined : appliedDocumentId || undefined,
    drilldownParams: drilldown?.params,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  });

  const handleCloseConfirmation = () => {
    setPackagerIdForDocumentsModal(null);
    setDrilldown(null);
  };

  // Clearing the drill-down returns to normal browsing for the same package
  // (keeps the modal open) rather than closing it.
  const handleClearDrilldown = () => {
    const pkgId = drilldown?.packageId;
    setDrilldown(null);
    setPackagerIdForDocumentsModal(pkgId ?? null);
  };

  const handleStatusClick = () => {
    // Handle status click if needed
  };

  const handleDocumentCountClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Handle document count click if needed
  };

  const getDocumentDetailsUrl = (documentId: string): string => {
    return `/data-pools/${dpid}/documents/${documentId}`;
  };

  const handleDocumentLinkClick = (documentId: string) => {
    const url = getDocumentDetailsUrl(documentId);
    window.open(url, '_blank');
  };

  return (
    <>
      <DialogGlynt
        open={!!effectivePackageId}
        closeModal={handleCloseConfirmation}
        cypressId="dialog-revert-bulk-corrections"
        width="1000px"
      >
        <DialogTitleGlynt
          title={`Package Documents: ${data?.count.toLocaleString() || 0}`}
          closeModal={handleCloseConfirmation}
          // startIcon="article"
        />
        <DialogContentGlynt>
          <DialogContentText sx={{ padding: '10px 3px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: drilldown ? 'flex-start' : 'flex-end',
                pb: 2,
              }}
            >
              {drilldown ? (
                <Chip
                  label={`Filtered: ${drilldown.label}`}
                  color="error"
                  variant="outlined"
                  onDelete={handleClearDrilldown}
                  data-cy="chip-entries-drilldown-filter"
                />
              ) : (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl sx={{ mt: 0, width: 220 }} size="small" variant="outlined">
                    <InputLabel id="package-entry-status-label">Status</InputLabel>
                    <Select
                      id="package-entry-status"
                      labelId="package-entry-status-label"
                      variant="outlined"
                      value={statusInput || ''}
                      label="Status"
                      onChange={(e) => {
                        const value = e.target.value as PackageEntryStatus | '';
                        setStatusInput(value === '' ? undefined : (value as PackageEntryStatus));
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {Object.values(PackageEntryStatus).map((s) => (
                        <MenuItem key={s} value={s}>
                          {formatEnumToLabel(s)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ mt: 0, width: '14ch' }}>
                    <TextField
                      id="doc-id-search"
                      size="small"
                      variant="outlined"
                      label="Document ID"
                      value={docIdInput}
                      onChange={(e) => setDocIdInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setAppliedStatus(statusInput);
                          setAppliedDocumentId(docIdInput);
                          setPage(0);
                        }
                      }}
                      placeholder="DocID"
                    />
                  </FormControl>
                  <Box sx={{ mt: 0, width: 'auto' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={isLoading || isFetching}
                      onClick={() => {
                        setAppliedStatus(statusInput);
                        setAppliedDocumentId(docIdInput);
                        setPage(0);
                      }}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={standardTableStyles.containerSx}>
              <Table sx={standardTableStyles.tableSx}>
                <TableHead sx={standardTableStyles.tableHeadSx}>
                  <TableRow>
                    <TableCell align="center">Doc ID</TableCell>
                    <TableCell align="center">Filename</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Exclusion Reason</TableCell>
                    <TableCell align="center">Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  data-cy="extraction-batch-table-body"
                  sx={standardTableStyles.tableBodySx}
                >
                  {isLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell align="center">
                            <Skeleton
                              animation="wave"
                              variant="rounded"
                              height={14}
                              width={56}
                              sx={{ borderRadius: '4px', mx: 'auto' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Skeleton
                              animation="wave"
                              variant="rounded"
                              height={14}
                              width={[120, 100, 140, 110, 130][i]}
                              sx={{ borderRadius: '4px', mx: 'auto' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Skeleton
                              animation="wave"
                              variant="rounded"
                              height={14}
                              width={64}
                              sx={{ borderRadius: '4px', mx: 'auto' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Skeleton
                              animation="wave"
                              variant="rounded"
                              height={14}
                              width={72}
                              sx={{ borderRadius: '4px', mx: 'auto' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Skeleton
                              animation="wave"
                              variant="rounded"
                              height={14}
                              width={96}
                              sx={{ borderRadius: '4px', mx: 'auto' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                              <Skeleton
                                animation="wave"
                                variant="circular"
                                width={18}
                                height={18}
                              />
                              <Skeleton
                                animation="wave"
                                variant="circular"
                                width={18}
                                height={18}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    : null}

                  {!isLoading && !isFetching && (data?.results?.length ?? 0) > 0
                    ? data?.results?.map((p: PackageEntry) => {
                        const updatedDate = new Date(p.updated_at);
                        const docDataId = getIdFromUrl(p.document_data);
                        // Entry status is derived from DocumentData once, at package
                        // creation, and never re-derived — so an upstream exclusion
                        // leaves the entry still shipping until someone excludes it here.
                        const sourceDisagrees =
                          p.document_data_status === DocumentDataStatusFilters.EXCLUDED &&
                          p.status_in_package !== PackageEntryStatus.EXCLUDED;

                        return (
                          <TableRow key={p.id}>
                            <TableCell align="center">
                              <LinkTableStyled
                                to={`/data-pools/${dpid}/documents/${p.source_file_id}`}
                                data-cy={`link-doc-details-${p.source_file_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {p.source_file_id}
                              </LinkTableStyled>
                            </TableCell>
                            <TableCell align="center">{p?.source_file_name}</TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {p.status_in_package}
                                {sourceDisagrees && (
                                  <Tooltip
                                    placement="top"
                                    title={
                                      `Source data is ${p.document_data_status} but this entry is still ` +
                                      `${p.status_in_package}. Re-rendering will not change that \u2014 exclude ` +
                                      `the entry here if it should not be delivered.`
                                    }
                                  >
                                    <WarningAmberIcon
                                      fontSize="small"
                                      color="warning"
                                      data-cy={`icon-entry-source-mismatch-${p.id}`}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">{p.exclusion_reason || '-'}</TableCell>
                            <TableCell align="center">{updatedDate.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <PackageEntryStatusChange
                                  entryId={p.id}
                                  currentStatus={p.status_in_package}
                                  disabledReason={entryEditDisabledReason}
                                />
                                <DocumentDataStatusChange docDataId={docDataId} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : null}

                  {data?.results?.length === 0 && !isFetching && !isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                          No package entries found.
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              {(data?.count ?? 0) > 0 && (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 150]}
                  component="div"
                  count={data?.count ?? 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => {
                    setPage(newPage);
                  }}
                  onRowsPerPageChange={(e) => {
                    const value = Number(e.target.value);
                    setRowsPerPage(value);
                    setPage(0);
                  }}
                />
              )}
            </Box>
          </DialogContentText>
        </DialogContentGlynt>
      </DialogGlynt>
    </>
  );
};

export default PackagerDocuments;
